import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DriverLicence, LicenceClass, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['LTA_OFFICER'];
const CLASSES: LicenceClass[] = ['A', 'A1', 'B', 'BE', 'C', 'CE', 'D'];
const STATUSES = ['ACTIVE', 'EXPIRED', 'SUSPENDED', 'REVOKED'];

/**
 * M21 — driver licences: issue, renew, view demerit/medical state. There is
 * no GET /licence/:nin detail endpoint — the officer list already returns
 * the full DriverLicence row, so the detail modal is fed straight from the
 * row already in hand (same pattern as homeaffairs-portal's Firearms.tsx).
 */
export function Licences() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selected, setSelected] = useState<DriverLicence | null>(null);
  const [showIssue, setShowIssue] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/transport/licence?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<DriverLicence>>(path);

  return (
    <div>
      <PageHeader
        title="Driver Licences"
        subtitle="Issue new licences and process renewals against demerit/medical/fines blockers (M21 — LTA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-44" placeholder="NIN contains…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isOfficer ? (
              <button className="btn-primary" onClick={() => setShowIssue(true)}>
                + Issue licence
              </button>
            ) : null}
          </>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No driver licences match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Classes</th>
                <th className="table-head">Demerits</th>
                <th className="table-head">Expires</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((l) => (
                <tr key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(l)}>
                  <td className="table-cell font-mono text-xs font-medium">{l.nin}</td>
                  <td className="table-cell text-xs">{l.classes.join(', ')}</td>
                  <td className="table-cell tabular-nums text-xs">{l.demeritBalance}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(l.expiresAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={l.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showIssue ? (
        <IssueLicenceModal
          onClose={() => setShowIssue(false)}
          onCreated={() => {
            setShowIssue(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selected ? (
        <LicenceDetailModal
          licence={selected}
          canAct={isOfficer}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function IssueLicenceModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [nin, setNin] = useState('');
  const [classes, setClasses] = useState<LicenceClass[]>([]);
  const [medicalCertRequired, setMedicalCertRequired] = useState(false);
  const [medicalCertRef, setMedicalCertRef] = useState('');
  const [physicalCardRequested, setPhysicalCardRequested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (c: LicenceClass) => setClasses((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/transport/licence', {
        nin: nin.trim(),
        classes,
        medicalCertRequired,
        ...(medicalCertRef.trim() ? { medicalCertRef: medicalCertRef.trim() } : {}),
        physicalCardRequested,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue licence');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Issue a driver licence" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <p className="mb-3 text-xs text-slate-500">Requires a passed practical test on record for every requested class.</p>
      <div className="space-y-3">
        <Field label="NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Licence classes">
          <div className="flex flex-wrap gap-1.5">
            {CLASSES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggle(c)}
                className={`rounded-lg border px-2.5 py-1 text-xs font-semibold ${
                  classes.includes(c) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={medicalCertRequired} onChange={(e) => setMedicalCertRequired(e.target.checked)} />
          Medical certificate required (heavy/PSV classes)
        </label>
        {medicalCertRequired ? (
          <Field label="Medical certificate ref">
            <input className="field-input" value={medicalCertRef} onChange={(e) => setMedicalCertRef(e.target.value)} />
          </Field>
        ) : null}
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={physicalCardRequested} onChange={(e) => setPhysicalCardRequested(e.target.checked)} />
          Physical card requested
        </label>
      </div>
      <button className="btn-primary mt-4" disabled={busy || nin.trim().length < 5 || classes.length === 0} onClick={submit}>
        {busy ? 'Issuing…' : 'Issue licence'}
      </button>
    </Modal>
  );
}

function LicenceDetailModal({
  licence,
  canAct,
  onClose,
  onChanged,
}: {
  licence: DriverLicence;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<string[] | null>(null);

  const renew = async () => {
    setBusy(true);
    setError(null);
    setBlockers(null);
    try {
      await api.post(`/api/v1/transport/licence/${encodeURIComponent(licence.nin)}/renew`);
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'RENEWAL_BLOCKED') {
        const details = e.details as { blockers?: string[] } | undefined;
        setBlockers(details?.blockers ?? ['UNKNOWN']);
        setError(e.message);
      } else {
        setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Renewal failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Driver licence — ${licence.nin}`} onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
          {blockers ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {blockers.map((b) => (
                <span key={b} className="rounded bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                  {b.replaceAll('_', ' ')}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Detail label="Status" value={<StatusBadge status={licence.status} />} />
          <Detail label="Classes" value={licence.classes.join(', ')} />
          <Detail label="Issued" value={formatDate(licence.issuedAt)} />
          <Detail label="Expires" value={formatDate(licence.expiresAt)} />
          <Detail label="Demerit balance" value={String(licence.demeritBalance)} />
          <Detail label="Medical cert" value={licence.medicalCertRequired ? (licence.medicalCertRef ?? 'Required — none on file') : 'Not required'} />
          {licence.medicalCertExpiresAt ? <Detail label="Medical cert expires" value={formatDate(licence.medicalCertExpiresAt)} /> : null}
          <Detail label="Physical card" value={licence.physicalCardRequested ? 'Requested' : 'Not requested'} />
          {licence.docVaultRef ? <Detail label="DocVault ref" value={licence.docVaultRef} mono /> : null}
        </div>
        {licence.suspendReason ? <ErrorNote message={`Suspended: ${licence.suspendReason}${licence.suspendedUntil ? ` (until ${formatDate(licence.suspendedUntil)})` : ''}`} /> : null}
        {licence.revokedReason ? <ErrorNote message={`Revoked: ${licence.revokedReason}`} /> : null}

        {canAct ? (
          licence.status === 'REVOKED' ? (
            <div className="text-xs text-slate-400">Revoked licences cannot be renewed from this console.</div>
          ) : (
            <button className="btn-success" disabled={busy} onClick={renew}>
              {busy ? 'Renewing…' : 'Renew licence'}
            </button>
          )
        ) : (
          <RoleNotice roles={OFFICER_ROLES} />
        )}

        <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
          Filed {formatDateTime(licence.createdAt)} · last updated {formatDateTime(licence.updatedAt)}
        </div>
      </div>
    </Modal>
  );
}
